<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text>
      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-eye-off</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="subtitle-2 font-weight-bold">Anonymous Feedback Received</div>
          <div class="caption grey--text">From colleagues — identities hidden</div>
        </div>
        <v-btn icon small :loading="loading" @click="load" title="Refresh">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Inbox / Sent tabs -->
      <div class="d-flex align-center mb-4" style="gap:8px;">
        <v-btn
          depressed
          :color="tab === 0 ? 'primary' : 'grey lighten-3'"
          :class="tab === 0 ? 'white--text' : 'grey--text text--darken-1'"
          class="text-none rounded-lg"
          small
          style="border: 2px solid; border-color: transparent;"
          :style="tab === 0 ? 'border-color:#1976d2' : ''"
          @click="tab = 0"
        >
          <v-icon small left>mdi-inbox</v-icon>
          Inbox ({{ anonInbox.length }})
          <v-badge v-if="unreadAnon > 0" :content="String(unreadAnon)" color="error" inline class="ml-2" />
        </v-btn>
        <v-btn
          depressed
          :color="tab === 1 ? 'primary' : 'grey lighten-3'"
          :class="tab === 1 ? 'white--text' : 'grey--text text--darken-1'"
          class="text-none rounded-lg"
          small
          @click="tab = 1"
        >
          <v-icon small left>mdi-send</v-icon>
          Sent ({{ sent.length }})
        </v-btn>
      </div>

      <!-- Inbox items -->
      <div v-if="tab === 0">
        <div v-if="anonInbox.length === 0" class="caption grey--text text-center py-6">No anonymous feedback received yet.</div>
        <div
          v-for="n in anonInbox"
          :key="n.id"
          class="pa-3 rounded mb-2"
          :style="n.read
            ? 'background:#f5f5f5; border:1px solid #e0e0e0;'
            : 'background:#fff8f0; border:1px solid #ffe0b2;'"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center" style="gap:8px;">
              <v-chip x-small color="orange lighten-4" class="orange--text text--darken-3 font-weight-medium">
                {{ n.category }}
              </v-chip>
              <span v-if="!n.read" style="width:8px;height:8px;border-radius:50%;background:#ff9800;display:inline-block;"></span>
            </div>
            <div class="d-flex align-center" style="gap:8px;">
              <span class="caption grey--text">Anonymous • {{ formatDate(n.date) }}</span>
              <v-btn v-if="!n.read" x-small text color="primary" class="text-none font-weight-bold" @click="markAnonRead(n)">
                Mark read
              </v-btn>
            </div>
          </div>
          <div class="body-2 mt-1" style="line-height:1.5;">{{ n.message }}</div>
        </div>
      </div>

      <!-- Sent items -->
      <div v-if="tab === 1">
        <div v-if="sent.length === 0" class="caption grey--text text-center py-6">No feedback sent yet.</div>
        <div
          v-for="s in sent"
          :key="s.id"
          class="pa-3 rounded mb-2"
          style="background:#f5f5f5; border:1px solid #e0e0e0;"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <v-chip x-small color="orange lighten-4" class="orange--text text--darken-3 font-weight-medium">
              {{ s.category }}
            </v-chip>
            <span class="caption grey--text">To {{ s.to }} • {{ formatDate(s.date) }}</span>
          </div>
          <div class="body-2 mt-1" style="line-height:1.5;">{{ s.message }}</div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'AnonFeedbackCard',
  data() {
    return {
      loading: false,
      tab: 0,
      sent: [],
      anonInbox: [],
    };
  },
  computed: {
    unreadAnon() { return this.anonInbox.filter(n => !n.read).length; },
  },
  mounted() {
    this.load();
    // Refresh when this user sends feedback (updates Sent tab immediately)
    // or when the global growth state changes.
    this.$root.$on('growth:feedback:sent', this.load);
    this.$root.$on('growth:refresh', this.load);
  },
  beforeDestroy() {
    this.$root.$off('growth:feedback:sent', this.load);
    this.$root.$off('growth:refresh', this.load);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [inboxRes, sentRes] = await Promise.all([
          this.$axios.get('api/employee-growth/feedback/anon'),
          this.$axios.get('api/employee-growth/feedback/sent'),
        ]);
        if (inboxRes.data.response) {
          this.anonInbox = inboxRes.data.data.map(n => ({
            id: n.id, category: n.category, message: n.message,
            date: n.created_at, read: !!n.is_read,
          }));
        }
        if (sentRes.data.response) {
          this.sent = sentRes.data.data.map(n => ({
            id: n.id, category: n.category, message: n.message,
            date: n.created_at, to: n.recipient?.name || 'Recipient',
          }));
        }
      } finally {
        this.loading = false;
      }
    },
    async markAnonRead(note) {
      try {
        await this.$axios.post(`api/employee-growth/feedback/${note.id}/read`);
        note.read = true;
      } catch (e) { note.read = true; }
    },
    formatDate(iso) {
      // $date/$tz so this follows the display timezone and clock settings.
      const d = this.$date(iso);
      if (!d.isValid()) return iso;
      return d.format(`MMM D, YYYY ${this.$tz.time}`);
    },
  },
};
</script>
